import { requestJson } from "./http/httpClient";
import { readArray, readInteger, readNullableString, readObject, readString } from "./http/jsonDecoding";

/**
 * The connector's release channel on this server (O-5, docs/plans/connector-installer.md CI-9):
 * the installers and packages of the offered release, which the release job built. Route:
 * `server/src/dmw/api/routes/connectors/releases.py`.
 */
export interface ConnectorDownload {
  /** `windows`, `darwin` or `linux`, as the release job names them. */
  readonly os: string;
  /** `x86_64` or `aarch64`. */
  readonly arch: string;
  /**
   * `installer`, downloaded and opened; or `package`, which the install script downloads for
   * the PC's CPU (docs/connector-release.md RE-7). Releases from before packages name no kind:
   * they had installers only.
   */
  readonly kind: "installer" | "package";
  readonly file: string;
  readonly size: number;
  readonly sha256: string;
  readonly url: string;
}

export interface ConnectorDownloads {
  /** `null` while this server offers no release. */
  readonly version: string | null;
  readonly downloads: readonly ConnectorDownload[];
  /** The install script's address (`curl -fsSL <it> | sh`), or `null` where none is served. */
  readonly installScript: string | null;
}

export function readConnectorDownloads(signal?: AbortSignal): Promise<ConnectorDownloads> {
  return requestJson({
    method: "GET",
    path: "/connectors/releases/downloads",
    signal,
    decodeResponse: (body) => {
      const object = readObject(body, "connector downloads");
      return {
        version: readNullableString(object, "version"),
        downloads: readArray(object["downloads"], "downloads", (item) => {
          const download = readObject(item, "download");
          return {
            os: readString(download, "os"),
            arch: readString(download, "arch"),
            kind: download["kind"] === "package" ? "package" : "installer",
            file: readString(download, "file"),
            size: readInteger(download, "size"),
            sha256: readString(download, "sha256"),
            url: readString(download, "url"),
          };
        }),
        // A server from before the install script sends none.
        installScript: object["install_script"] === undefined ? null : readNullableString(object, "install_script"),
      };
    },
  });
}
